import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import TimeAgo from 'javascript-time-ago';
import en from 'javascript-time-ago/locale/en';
import { withTheme } from 'styled-components';
import {
  List,
  ListBody,
  ListHeader,
  ListHeaderItem,
  ListItem,
  ListItemColumn,
  MaterialIcon,
  SVGIcon,
} from '@pearsontechnology/aero-components';
import CommentSummary from 'components/CommentsSummary/courseLevelCommentSummary';
import { featureKeys } from 'services/FeatureFlagService';
import { intl } from 'containers/LanguageProvider/IntlGlobalProvider';
import enhanceWithNavigation from 'hocs/enhanceWithNavigation';
import meShape from 'Workspace/types/me';
import { ActionTypes } from 'constants/enums';
import pluginTypes from 'constants/pluginTypes';
import { timeFormats, InputKeys } from 'constants/constants';
import {
  ActivityName,
  CourseType,
  ColoredText,
  ProjectColumnText,
  StyledFavouriteIcon,
  ActivityNameWrapper,
  ActivityWorkspaceProjectName,
  ActivityNameNew,
  UpdatedAtSortHeader,
  WorkspaceRedesignList,
  WorkspaceRedesignHeader,
  WorkspaceRedesignItem,
  CourseInfoWrapper,
  TextWrapper,
  AnimatedChipThin,
} from './styles';
import { getColor, getIcon, getCourseWareNameFromPlugin } from './uiHelpers';
import messages from './messages';
import ProjectActivityListActions from '../ProjectActivityListActions';
import DeleteConfirm from '../DeleteConfirm';
import { getTime } from '../../utils';
import enhanceWithFeatureFlagSubscription from 'hocs/enhanceWithFeatureFlagSubscription';
import PublishStatusIcon from '../PublishStatusIcon';
import { getActivityPath } from 'Workspace/Projects/utils';
import { getMarketDerivatives } from 'builder/utils';

TimeAgo.locale(en);

const timeAgo = new TimeAgo('en-US');
const getElapsedTime = (date) => timeAgo.format(new Date(date));
const LinkListItem = enhanceWithNavigation(ListItem);

function swallowEvent(event) {
  event.stopPropagation();
}


const ProjectActivityList = ({
  activityList,
  activitiesCurrentlyIngesting,
  theme,
  currentUser,
  onAction,
  toggleFavourite,
  annotationService,
  sortActivityListByDate,
  sortOrder,
  isWorkspaceRedesignPage,
  isRecentPage,
  getFeatureFlagValue,
}) => {
  const [isDeleteConfirmVisible, setDeleteConfirmVisible] = useState(false);
  const [deleteConfirmMessages, setDeleteConfirmMessages] = useState();
  const [isCommentAggregationEnabled, setIsCommentAggregationEnabled] = useState(false);
  const [isDisplayCreatedDate, setIsDisplayCreatedDate] = useState(false);
  const [isCommentPopUpSummaryEnabled, setIsCommentPopUpSummaryEnabled] = useState(false);
  const [activityListWithMarkets, setActivityListWithMarkets] = useState(activityList);
  const isBRNT18710WSRedesignFFEnabled = getFeatureFlagValue(featureKeys.BRNT_18710_WORKSPACE_REDESIGN_22);
  const isWorkspaceRedesignFFEnabled = getFeatureFlagValue(featureKeys.WORKSPACE_REDESIGN);
  const isCommentAggregationFFEnabled = getFeatureFlagValue(featureKeys.COMMENT_AGGREGATION_AND_COLLABORATIVE_EDITING);
  const isDisplayCreatedDateProjectViewFFEnabled = getFeatureFlagValue(featureKeys.DISPLAY_CREATED_DATE_PROJECT_VIEW);
  const isCommentPopupSummaryFFEnabled = getFeatureFlagValue(featureKeys.COMMENT_AGGREGATION_AND_COLLABORATIVE_EDITING_POP_UP_SUMMARY);
  const isOculusStatusEnabled = getFeatureFlagValue(featureKeys.WORKSPACE_ACTIVITY_LIST_OCULUS_STATUS);

  const activityInAction = useRef();
  const openDeleteConfirm = () => {
    setDeleteConfirmVisible(true);
  };

  const closeDeleteConfirm = () => {
    setDeleteConfirmVisible(false);
  };

  const handleDeleteActivity = () => {
    onAction(ActionTypes.DELETE, activityInAction.current);
    closeDeleteConfirm();
  };

  const onClickItemAction = (type, activity) => {
    activityInAction.current = activity;
    if (type === ActionTypes.DELETE) {
      switch (activity.plugin.type) {
        case pluginTypes.COURSE:
          setDeleteConfirmMessages(messages.courseDeleteMessages);
          break;
        case pluginTypes.LESSON:
          setDeleteConfirmMessages(messages.lessonDeleteMessages);
          break;
      }
      openDeleteConfirm();
    } else {
      onAction(type, activity);
    }
  };

  const handleDateSortKeyDown = (event) => {
    const { key } = event;
    if (key === InputKeys.ENTER_KEY) {
      sortActivityListByDate();
    }
  };

  useEffect(() => {
    const isEnabled = !isWorkspaceRedesignPage && isCommentAggregationFFEnabled;
    const isCreatedDateEnabled = !isRecentPage && isDisplayCreatedDateProjectViewFFEnabled;
    const isCommentSummaryEnabled = !isWorkspaceRedesignPage && isCommentPopupSummaryFFEnabled;
    setIsDisplayCreatedDate(isCreatedDateEnabled);
    setIsCommentAggregationEnabled(isEnabled);
    setIsCommentPopUpSummaryEnabled(isCommentSummaryEnabled);

    const getPublicationMarkets = async () => {
      // I believe all of this code will be removed/updated once BACW-746 comes in.
      const tempActivityListWithMarkets = await Promise.all(
        activityList.map(async (activity) => {
          const markets = await getMarketDerivatives(activity.activityId);
          if (markets.length > 0 && (activity.publicationDetails && !Array.isArray(activity.publicationDetails))) {
            return {
              ...activity,
              publicationDetails: {
                ...activity.publicationDetails,
                markets: markets,
              },
            };
          }
          return activity;
        })
      );
      setActivityListWithMarkets(tempActivityListWithMarkets);
    };
    getPublicationMarkets();
  }, [isWorkspaceRedesignPage, isRecentPage, isCommentAggregationFFEnabled, isDisplayCreatedDateProjectViewFFEnabled, isCommentPopupSummaryFFEnabled, activityList]);

  const nameWidth = isDisplayCreatedDate && isCommentAggregationEnabled
    ? "30%"
    : (isDisplayCreatedDate || isCommentAggregationEnabled) ? "32%" : "38%";

  const commentsBodyWidth = (!isDisplayCreatedDate && isCommentAggregationEnabled) ? "11%" : "10%";
  const commentsHeaderWidth = (!isDisplayCreatedDate && isCommentAggregationEnabled) ? "7%" : "8%";

  const ownerWidth = ((isDisplayCreatedDate && isCommentAggregationEnabled) || isWorkspaceRedesignPage)
    ? "28%"
    : (isDisplayCreatedDate || isCommentAggregationEnabled) ? "31%" : "37%";

  const lastModifiedWidth = isDisplayCreatedDate && isCommentAggregationEnabled
    ? "10%"
    : isDisplayCreatedDate ? "10%" : "12%";

  const createdOnWidth = isDisplayCreatedDate && isCommentAggregationEnabled
    ? "10%"
    : (isDisplayCreatedDate && "11%");

  const getCourseWare = (plugin) => {
    const pluginName = getCourseWareNameFromPlugin(plugin);
    return pluginName && <CourseType>{pluginName}</CourseType>;
  };


  return (
    <List as={isWorkspaceRedesignPage && WorkspaceRedesignList}>
      <ListHeader as={isWorkspaceRedesignPage && WorkspaceRedesignHeader}>
        <ListItemColumn colWidth={nameWidth} colWidthMobile="100%">
          <ListHeaderItem>{intl.formatMessage(messages.name)}</ListHeaderItem>
        </ListItemColumn>
        {(isCommentAggregationEnabled && isCommentPopUpSummaryEnabled) && <ListItemColumn style={{ display: "flex", alignItems: "center", justifyContent: "center" }} colWidth={commentsHeaderWidth} colWidthMobile="10%">
          <ListHeaderItem>{intl.formatMessage(messages.comments)}</ListHeaderItem>
        </ListItemColumn>}
        <ListItemColumn colWidth={ownerWidth} colWidthMobile="50%">
          <ListHeaderItem>{intl.formatMessage(messages.owner)}</ListHeaderItem>
        </ListItemColumn>
        {!isWorkspaceRedesignPage && <ListItemColumn colWidth={lastModifiedWidth} colWidthMobile="50%">
          <ListHeaderItem>{intl.formatMessage(messages.updatedAt)}
          </ListHeaderItem>
        </ListItemColumn>}
        {isWorkspaceRedesignPage && <ListItemColumn
          as={UpdatedAtSortHeader}
          tabIndex="0"
          colWidth={lastModifiedWidth}
          colWidthMobile="50%"
          onKeyDown={handleDateSortKeyDown}
          onClick={sortActivityListByDate}
        >
          <ListHeaderItem>{isRecentPage ? intl.formatMessage(messages.dateOpened) : intl.formatMessage(messages.dateUpdated)}
          </ListHeaderItem>
          <SVGIcon
            icon={sortOrder === 'asc' ? 'arrowDownSort' : 'arrowUpSort'}
            viewBox="0 0 24 24"
            width={{ width: '24px', height: '24px' }}
          />
        </ListItemColumn>}
        {isDisplayCreatedDate && <ListItemColumn colWidth={createdOnWidth}>
          <ListHeaderItem>{intl.formatMessage(messages.createdOn)}</ListHeaderItem>
        </ListItemColumn>}
        {(isBRNT18710WSRedesignFFEnabled || isWorkspaceRedesignFFEnabled) &&
          <ListItemColumn colWidth={isWorkspaceRedesignPage ? "60px" : "30px"} colWidthMobile="30px" grow="0">
          </ListItemColumn>}
        {!isWorkspaceRedesignPage && <ListItemColumn colWidth="30px" colWidthMobile="30px" grow="0">
          <div style={{ width: 30 }} />
        </ListItemColumn>}
      </ListHeader>
      <ListBody>
        {activityListWithMarkets
          .map((activity) => (
            <LinkListItem as={isWorkspaceRedesignPage && WorkspaceRedesignItem}
              handleKeyDownEvent={true}
              tabIndex={isWorkspaceRedesignPage ? "0" : undefined}
              key={`courseware-item-${activity.activityId}`}
              path={getActivityPath(activity)}
              disable={activitiesCurrentlyIngesting?.includes(activity.activityId)}
            >
              <ListItemColumn colWidth={nameWidth} colWidthMobile="100%">
                <ColoredText workspaceRedesign={isWorkspaceRedesignPage} color={getColor(activity.plugin.type, theme)}>
                  <SVGIcon
                    icon={getIcon(activity.plugin.type)}
                    viewBox="0 0 24 24"
                    width={{ width: '24px', height: '24px' }}
                  />
                </ColoredText>
                {isWorkspaceRedesignPage && <ActivityNameWrapper>
                  <ActivityWorkspaceProjectName>
                    {activity.workspaceName} / {activity.projectName}
                  </ActivityWorkspaceProjectName>
                  <ActivityNameNew>
                    {activity.configFields.title || activity.activityId}
                  </ActivityNameNew>
                </ActivityNameWrapper>}
                {!isWorkspaceRedesignPage && (
                  <ActivityName>
                    {activity.configFields.title || activity.activityId}
                    {activitiesCurrentlyIngesting?.includes(activity.activityId) ? 
                      <AnimatedChipThin>
                        <TextWrapper>{intl.formatMessage(messages.importing)}</TextWrapper>
                      </AnimatedChipThin>
                      : ''}
                    <CourseInfoWrapper>
                      {getCourseWare(activity.plugin)}
                      {isOculusStatusEnabled && <PublishStatusIcon activity={activity} theme={theme} />}
                    </CourseInfoWrapper>
                  </ActivityName>
                )}
              </ListItemColumn>
              {(isCommentAggregationEnabled && isCommentPopUpSummaryEnabled) && <ListItemColumn style={{ display: "flex", justifyContent: "center" }}
                colWidth={commentsBodyWidth} colWidthMobile="10%" grow="0" onClick={swallowEvent} >
                <CommentSummary annotationService={annotationService} rootElementId={activity.activityId} />
              </ListItemColumn>}
              <ListItemColumn colWidth={ownerWidth} colWidthMobile="0%">
                <ProjectColumnText>{activity.creator && activity.creator.email}</ProjectColumnText>
              </ListItemColumn>
              <ListItemColumn colWidth={lastModifiedWidth} colWidthMobile="50%">
                <ProjectColumnText>
                  {isRecentPage ? activity.dateOpened : getElapsedTime(activity.updatedAt || activity.createdAt)}
                </ProjectColumnText>
              </ListItemColumn>
              {isDisplayCreatedDate && <ListItemColumn colWidth={createdOnWidth} >
                <ProjectColumnText>
                  {getTime(activity.createdAt, timeFormats.DATE_MONTH)}
                </ProjectColumnText>
              </ListItemColumn>}
              {(isBRNT18710WSRedesignFFEnabled || isWorkspaceRedesignFFEnabled) &&
                <ListItemColumn colWidth={isWorkspaceRedesignPage ? "60px" : "30px"} colWidthMobile="30px" grow="0" onClick={swallowEvent} >
                  <StyledFavouriteIcon tabIndex="0">
                    <MaterialIcon id={activity.isFavourite ? 'star' : 'star_border'} onClick={() => toggleFavourite(activity)} />
                  </StyledFavouriteIcon>
                </ListItemColumn>}
              {!isWorkspaceRedesignPage && <ListItemColumn colWidth="30px" colWidthMobile="30px" grow="0" onClick={swallowEvent} >
                <ProjectActivityListActions
                  activity={activity}
                  currentUser={currentUser}
                  onAction={onClickItemAction}
                />
              </ListItemColumn>}
            </LinkListItem>
          ))}
        <DeleteConfirm visible={isDeleteConfirmVisible} messages={deleteConfirmMessages} onDismiss={closeDeleteConfirm} handleDeleteProject={handleDeleteActivity} />
      </ListBody>
    </List>
  );
};

ProjectActivityList.propTypes = {
  activityList: PropTypes.array,
  theme: PropTypes.object,
  currentUser: meShape,
  onAction: PropTypes.func,
};

const flagNames = [
  featureKeys.BRNT_18710_WORKSPACE_REDESIGN_22,
  featureKeys.WORKSPACE_REDESIGN,
  featureKeys.COMMENT_AGGREGATION_AND_COLLABORATIVE_EDITING,
  featureKeys.DISPLAY_CREATED_DATE_PROJECT_VIEW,
  featureKeys.COMMENT_AGGREGATION_AND_COLLABORATIVE_EDITING_POP_UP_SUMMARY,
];

export default withTheme(enhanceWithFeatureFlagSubscription(ProjectActivityList, flagNames));
